import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";

import type { Trade, TradeFill, WalletTier } from "../api/types";
import { Empty, Panel } from "../components/Panel";
import { PolymarketProfile, Tier } from "../components/Tier";
import { useTrades } from "../hooks/useTrades";
import { accountLabel } from "../lib/accounts";
import { formatTime, shortId } from "../lib/format";
import {
  counterpartyFills,
  fillTier,
  isMaker,
  largestFill,
  legacyAddresses,
  neighbourFills,
  ourFill,
  ourFee,
  ourNotional,
  ourOutcome,
  ourSide,
  summarize,
  takerAddress,
  tradeKey,
} from "../lib/trades";
import { useAccounts } from "../store/useAccounts";
import { useTiers } from "../store/useTiers";

/**
 * История наших сделок и контрагентов (веха M6).
 *
 * # Зачем отдельный экран, когда есть лента
 *
 * Лента — скользящее окно: web_api повторяет при подключении две сотни событий
 * шины, вкладка держит тысячу, и на живом рынке это порядка двух минут. Сделка,
 * случившаяся утром, уходит из неё навсегда, а вместе с ней — единственный
 * экранный след контрагента. Разбирают же контрагента ПОТОМ, и разбирать его
 * надо по журналу, который переживает и вкладку, и рестарт трейдера.
 *
 * # Что здесь показано и почему именно так
 *
 * Наш филл, а не сделка. Событие приватного канала описывает тейкер-ордер
 * целиком, поэтому цена и размер сделки — это цена и размер агрессора; в
 * мейкерской сделке они относятся к другому исходу и к чужим деньгам.
 *
 * Контрагент — только настоящий. Когда мейкером были мы, встречные строки
 * принадлежат соседям по сносу, а не нашей стороне: агрессора на проводе нет
 * (ADR-0007 §1). Поэтому такие сделки честно говорят «тейкер скрыт», а соседи
 * лежат в раскрытой строке под своим именем.
 */
export function TradesPage() {
  const [params, setParams] = useSearchParams();
  const market = params.get("market") ?? undefined;
  const wallet = params.get("wallet")?.toLowerCase() ?? undefined;
  const sinceHours = Number(params.get("hours") ?? 24) || undefined;

  const query = useTrades({ market, wallet, sinceHours });
  const trades = query.data?.trades ?? [];
  const summary = summarize(trades);

  const setFilter = (key: string, value: string | undefined) => {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value);
    else next.delete(key);
    setParams(next, { replace: true });
  };

  return (
    <div className="flex flex-col gap-2">
      <Panel
        title="Мои сделки"
        subtitle={query.data?.truncated ? "показаны не все" : "из журнала"}
        actions={<Period hours={sinceHours} onChange={(h) => setFilter("hours", String(h))} />}
      >
        <div className="flex flex-wrap items-center gap-x-6 gap-y-1 text-xs">
          <Stat label="сделок" value={String(summary.count)} />
          <Stat label="мейкером" value={`${summary.makerCount} из ${summary.count}`} />
          <Stat label="наш оборот" value={`$${summary.notional}`} />
          {/* Комиссия — рядом с оборотом, а не в конце строки: это единственная
              пара чисел, которую здесь сравнивают. Мейкерские сделки в неё не
              попадают вовсе — комиссию платит тейкер, и в этом весь смысл
              показа: видно, сколько стоило нетерпение за период. */}
          <Stat
            label="комиссия"
            value={`$${summary.fees}`}
            title={
              summary.feesUnknown > 0
                ? `сумма неполна: у ${summary.feesUnknown} сделок расписание рынка не было известно`
                : "заплачено нами; в мейкерских сделках комиссию платит контрагент"
            }
          />
          {/* «С учётом», а не «после»: покупка платит комиссию СВЕРХ нотионала, и
              «после» читалось бы как вычитание в обе стороны. */}
          <Stat
            label="оборот с учётом комиссии"
            value={`$${summary.notionalNet}`}
            title="покупки дороже нотионала на комиссию, продажи — дешевле выручки"
          />
          {summary.feesUnknown > 0 && (
            <span className="text-amber-300" title="комиссия таких сделок не посчитана">
              без комиссии {summary.feesUnknown}
            </span>
          )}
          <Stat label="контрагентов" value={String(summary.wallets)} />
          {wallet && (
            <button
              onClick={() => setFilter("wallet", undefined)}
              className="text-[var(--color-accent)] hover:underline"
              title="снять фильтр по кошельку"
            >
              кошелёк {shortId(wallet)} ✕
            </button>
          )}
          {market && (
            <button
              onClick={() => setFilter("market", undefined)}
              className="text-[var(--color-accent)] hover:underline"
              title="снять фильтр по рынку"
            >
              рынок {shortId(market)} ✕
            </button>
          )}
        </div>
        {/* «Контрагентов» считается ТОЛЬКО по тейкерским сделкам: в мейкерских
            другой стороны в данных нет, и складывать туда соседей по сносу
            значило бы отчитаться о встречах, которых не было. */}
      </Panel>

      <Panel title="История" subtitle={periodLabel(sinceHours)}>
        {query.isLoading && !query.data ? (
          <Empty>загрузка…</Empty>
        ) : query.error ? (
          <p className="text-sm text-[var(--color-sell)]">{(query.error as Error).message}</p>
        ) : trades.length === 0 ? (
          <Empty>
            За выбранный период сделок не было. История наполняется исполнениями: в неё
            попадает то, что действительно исполнилось, а не выставленные ордера.
          </Empty>
        ) : (
          <TradesTable trades={trades} />
        )}
      </Panel>
    </div>
  );
}

function Stat({ label, value, title }: { label: string; value: string; title?: string }) {
  return (
    <span className="text-[var(--color-muted)]" title={title}>
      {label} <span className="num text-[var(--color-fg)]">{value}</span>
    </span>
  );
}

const PERIODS: { hours: number | undefined; label: string }[] = [
  { hours: 1, label: "час" },
  { hours: 24, label: "сутки" },
  { hours: 24 * 7, label: "неделя" },
  { hours: undefined, label: "всё" },
];

function periodLabel(hours: number | undefined): string {
  return PERIODS.find((p) => p.hours === hours)?.label ?? `${hours} ч`;
}

function Period({
  hours,
  onChange,
}: {
  hours: number | undefined;
  onChange: (hours: number | "") => void;
}) {
  return (
    <div className="flex items-center gap-1 text-xs">
      {PERIODS.map((p) => (
        <button
          key={p.label}
          onClick={() => onChange(p.hours ?? "")}
          className={`rounded px-2 py-0.5 ${
            p.hours === hours
              ? "bg-[var(--color-accent)]/15 text-[var(--color-accent)]"
              : "text-[var(--color-muted)] hover:text-white"
          }`}
        >
          {p.label}
        </button>
      ))}
    </div>
  );
}

function TradesTable({ trades }: { trades: readonly Trade[] }) {
  // Раскрытая строка показывает ВСЕ встречные ордера сделки. В свёрнутом виде
  // помещается один, и этого хватает для взгляда, но не для разбора: «кто из
  // них стоит против меня каждый день» — вопрос ко всему списку.
  const [open, setOpen] = useState<Set<string>>(new Set());
  const toggle = (id: string) =>
    setOpen((prev) => {
      const next = new Set(prev);
      if (!next.delete(id)) next.add(id);
      return next;
    });

  const tiers = useTiers((s) => s.tiers);

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-xs">
        <thead>
          <tr className="text-left text-[var(--color-muted)]">
            <th className="pb-2 font-normal">Время</th>
            <th className="pb-2 font-normal">Рынок</th>
            <th className="pb-2 font-normal">Аккаунт</th>
            <th className="pb-2 font-normal">Роль</th>
            <th className="pb-2 font-normal">Сторона</th>
            <th className="pb-2 text-right font-normal">Наша цена</th>
            <th className="pb-2 text-right font-normal">Наш размер</th>
            <th className="pb-2 text-right font-normal">Сумма, $</th>
            <th className="pb-2 text-right font-normal">Комиссия, $</th>
            <th className="pb-2 font-normal">Контрагент</th>
            <th className="pb-2 font-normal">Статус</th>
          </tr>
        </thead>
        <tbody>
          {trades.map((t) => (
            <Row
              key={tradeKey(t)}
              trade={t}
              expanded={open.has(tradeKey(t))}
              onToggle={() => toggle(tradeKey(t))}
              tiers={tiers}
            />
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Row({
  trade,
  expanded,
  onToggle,
  tiers,
}: {
  trade: Trade;
  expanded: boolean;
  onToggle: () => void;
  tiers: Readonly<Record<string, WalletTier>>;
}) {
  const maker = isMaker(trade);
  const fill = ourFill(trade);
  const notional = ourNotional(trade);
  const counterparties = counterpartyFills(trade);
  const neighbours = neighbourFills(trade);
  const rows = counterparties.length > 0 ? counterparties : neighbours;

  /**
   * Наша сторона и наш исход, а не сторона сделки.
   *
   * side сделки описывает тейкер-ордер и в мейкерской сделке относится к
   * ЧУЖОМУ токену. Считалось это обращением стороны, что верно лишь для
   * обычного матча; на «чеканке» (тейкер покупает Down по 0.16, мейкеры
   * покупают Up по 0.84) обращение писало «продажа» напротив покупки, которую
   * оператор только что сделал кнопкой «Купить UP». Настоящий ответ лежит в
   * нашей встречной строке — см. lib/trades.ourSide.
   */
  const side = ourSide(trade);
  const outcome = ourOutcome(trade);
  const color = side === "BUY" ? "var(--color-buy)" : "var(--color-sell)";
  // Строкой, а не всем составом: опрос состава иначе перерисовывал бы таблицу.
  const account = useAccounts((s) => accountLabel(s.data, trade.account));

  return (
    <>
      <tr
        onClick={onToggle}
        className="cursor-pointer border-t border-[var(--color-border)] hover:bg-white/5"
        title="раскрыть встречные ордера"
      >
        <td className="num py-1 pr-3 text-[var(--color-muted)]">{formatTime(trade.timestamp)}</td>
        <td className="num py-1 pr-3 text-[var(--color-muted)]">
          <Link
            to={`/market/${encodeURIComponent(trade.condition_id)}`}
            onClick={(e) => e.stopPropagation()}
            className="hover:text-[var(--color-accent)] hover:underline"
          >
            {shortId(trade.condition_id)}
          </Link>
        </td>
        <td className="py-1 pr-3 text-[var(--color-muted)]" title={trade.account ?? "строка до бэкфилла"}>
          {account}
        </td>
        <td className="py-1 pr-3 text-[var(--color-muted)]">{maker ? "мейкер" : "тейкер"}</td>
        {/* Исход стоит рядом со стороной, а не в отдельной колонке: «покупка» без
            токена в бинарном рынке не значит ничего — покупка Up по 0.84 и есть
            продажа Down по 0.16, и без имени исхода цена в соседней ячейке
            повисает без системы отсчёта. */}
        <td className="py-1 pr-3" style={{ color }}>
          {side === "BUY" ? "покупка" : "продажа"}
          {outcome && <span className="ml-1 text-[var(--color-muted)]">{outcome}</span>}
        </td>
        <td className="num py-1 pr-3 text-right" style={{ color }}>
          {fill?.price ?? "—"}
        </td>
        <td className="num py-1 pr-3 text-right">{fill?.size ?? "—"}</td>
        <td className="num py-1 pr-3 text-right text-[var(--color-muted)]">{notional ?? "—"}</td>
        <td className="num py-1 pr-3 text-right">
          <Fee trade={trade} />
        </td>
        <td className="py-1 pr-3">
          <Counterparty trade={trade} tiers={tiers} />
        </td>
        <td className="py-1 text-[var(--color-muted)]">{trade.status ?? "—"}</td>
      </tr>
      {expanded && (
        <tr className="border-t border-[var(--color-border)] bg-black/20">
          <td colSpan={11} className="px-2 py-2">
            <Fills trade={trade} fills={rows} neighbours={counterparties.length === 0} tiers={tiers} />
          </td>
        </tr>
      )}
    </>
  );
}

/**
 * Ячейка комиссии.
 *
 * Три состояния, и они обязаны различаться:
 *
 *   - мы были тейкером — заплатили, и сумма стоит числом;
 *   - мы были мейкером — заплатил контрагент, и здесь ПРОЧЕРК со подсказкой, а не
 *     «$0»: ноль читался бы как «сделка обошлась бесплатно», тогда как деньги
 *     заплачены, просто не нами;
 *   - комиссия не посчитана (расписание рынка не было известно) — вопросительный
 *     знак. Показать её нулём значило бы соврать в ту же сторону, из-за которой
 *     веха M7 и появилась.
 */
function Fee({ trade }: { trade: Trade }) {
  const fee = ourFee(trade);
  if (fee === null) {
    return (
      <span
        className="text-amber-300"
        title="комиссия не посчитана: расписание рынка не было известно в момент сделки"
      >
        ?
      </span>
    );
  }
  const paid = Number(fee) > 0;
  if (!paid) {
    return (
      <span
        className="text-[var(--color-muted)]"
        title={
          trade.fee === undefined
            ? "комиссию платит тейкер; наша доля ноль"
            : `комиссию платит тейкер: ${trade.fee} USD заплатил контрагент, не наш аккаунт`
        }
      >
        —
      </span>
    );
  }
  return (
    <span className="text-[var(--color-sell)]" title="комиссия тейкера, заплачена нами">
      {fee}
    </span>
  );
}

/**
 * Ячейка контрагента.
 *
 * Три разных состояния, и они не должны выглядеть одинаково: у тейкерской
 * сделки контрагент есть и известен; у мейкерской его нет в данных вовсе; у
 * сделки без встречных строк не приехало ничего. Свести их к прочерку значило
 * бы выдать «мы не знаем» за «этого не было».
 */
function Counterparty({
  trade,
  tiers,
}: {
  trade: Trade;
  tiers: Readonly<Record<string, WalletTier>>;
}) {
  const fills = counterpartyFills(trade);
  if (fills.length === 0) {
    const legacy = legacyAddresses(trade);
    if (legacy.length > 0) {
      // Запись до вехи: адреса сохранились, роль сделки — нет. Показываем их
      // приглушённо и другим словом, потому что «контрагент» тут не доказан.
      return (
        <span
          className="text-[var(--color-muted)]"
          title={
            "Запись сделана до вехи M6: роль в сделке и суммы по каждому мейкеру не " +
            "сохранялись. Контрагентами эти адреса являются, только если тейкером были мы.\n" +
            legacy.join("\n")
          }
        >
          встречены: {legacy.length}
        </span>
      );
    }
    if (isMaker(trade)) {
      // Агрессор, восстановленный из квитанции транзакции (ADR-0008): на проводе
      // его нет, но цепочка знает обе стороны матча. Это НАСТОЯЩИЙ контрагент
      // мейкерской сделки — в отличие от встречных строк, где соседи по сносу.
      const taker = takerAddress(trade);
      if (taker) {
        return (
          <span className="inline-flex items-center gap-1.5">
            <Link
              to={`/wallet/${encodeURIComponent(taker)}`}
              onClick={(e) => e.stopPropagation()}
              title={`снял нашу заявку; адрес восстановлен из квитанции транзакции\n${taker}`}
              className="num hover:text-[var(--color-accent)] hover:underline"
            >
              {shortId(taker)}
            </Link>
            <Tier tier={tiers[taker]} title="тир на сейчас: на момент сделки адреса не было" />
            <PolymarketProfile address={taker} />
          </span>
        );
      }
      return (
        <span
          className="text-[var(--color-muted)]"
          title={
            "Мы стояли в стакане, и нас снял агрессор. На проводе видны только мейкеры, " +
            "адреса тейкера там нет вовсе — есть лишь его ордер " +
            (trade.taker_order_id ? shortId(trade.taker_order_id, 14) : "") +
            ". Адрес восстанавливается из квитанции транзакции и появится через " +
            "несколько секунд. В раскрытой строке — соседи по сносу, а не наша сторона."
          }
        >
          тейкер скрыт
        </span>
      );
    }
    return <span className="text-[var(--color-muted)]">—</span>;
  }

  const top = largestFill(fills);
  if (!top) return <span className="text-[var(--color-muted)]">—</span>;
  const { tier, snapshot } = fillTier(top, tiers);
  const extra = fills.length - 1;

  return (
    <span className="inline-flex items-center gap-1.5">
      <Link
        to={`/wallet/${encodeURIComponent(top.address)}`}
        onClick={(e) => e.stopPropagation()}
        title={top.address}
        className="num hover:text-[var(--color-accent)] hover:underline"
      >
        {shortId(top.address)}
      </Link>
      <Tier
        tier={tier}
        title={snapshot ? "квалификация на момент сделки" : "тир на сейчас: снимка не сохранилось"}
      />
      {extra > 0 && (
        <span className="text-[var(--color-muted)]" title="ещё контрагенты в этой сделке">
          +{extra}
        </span>
      )}
    </span>
  );
}

/** Все встречные ордера сделки: кто, сколько и по какой цене. */
function Fills({
  trade,
  fills,
  neighbours,
  tiers,
}: {
  trade: Trade;
  fills: readonly TradeFill[];
  neighbours: boolean;
  tiers: Readonly<Record<string, WalletTier>>;
}) {
  const ours = (trade.fills ?? []).filter((f) => f.ours);
  const legacy = legacyAddresses(trade);
  const taker = takerAddress(trade);

  // Запись до вехи: суммы по мейкерам и роль в сделке не сохранялись, и
  // восстановить их неоткуда — сырое событие в журнал не писалось. Показываем
  // то, что есть, ровно как «встреченные кошельки».
  if (legacy.length > 0) {
    return (
      <div className="flex flex-col gap-2">
        <p className="text-[var(--color-muted)]">
          Запись сделана до вехи M6: сохранились только адреса. Сколько каждый из них
          налил, по какой цене и кем в этой сделке были мы — не записывалось, и
          восстановить это неоткуда.
        </p>
        <div className="flex flex-wrap gap-x-4 gap-y-1">
          {legacy.map((address) => (
            <span key={address} className="inline-flex items-baseline gap-1.5">
              <Link
                to={`/wallet/${encodeURIComponent(address)}`}
                onClick={(e) => e.stopPropagation()}
                title={address}
                className="num hover:text-[var(--color-accent)] hover:underline"
              >
                {shortId(address, 16)}
              </Link>
              <PolymarketProfile address={address} />
            </span>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-2">
      {/* Список кошельков под строкой «тейкер скрыт» сам по себе читается как
          список контрагентов — по нему и спросили. Поэтому у мейкерской сделки
          первым идёт не описание, а прямое отрицание: эти адреса стояли РЯДОМ с
          нами, по нашу сторону, и их профили ничего не говорят о том, кто нас
          снял. */}
      {neighbours ? (
        <p className="text-[var(--color-muted)]">
          <span className="text-amber-300">Это не контрагенты.</span> Мы стояли в стакане, и
          нас снял чужой агрессор: он и есть другая сторона сделки, но его адреса на проводе
          нет вовсе — приватный канал показывает только мейкеров.{" "}
          {taker ? (
            // Восстановлен из цепочки (ADR-0008). Ссылка стоит именно здесь, в
            // абзаце про «его тут нет»: иначе объяснение противоречило бы
            // соседней колонке, где адрес уже показан.
            <>
              Адрес восстановлен из квитанции транзакции:{" "}
              <Link
                to={`/wallet/${encodeURIComponent(taker)}`}
                onClick={(e) => e.stopPropagation()}
                title={taker}
                className="num hover:text-[var(--color-accent)] hover:underline"
              >
                {shortId(taker, 16)}
              </Link>{" "}
              <PolymarketProfile address={taker} />
              {trade.taker_order_id && (
                <>
                  , ордер <span className="num">{shortId(trade.taker_order_id, 14)}</span>
                </>
              )}
              .{" "}
            </>
          ) : (
            trade.taker_order_id && (
              <>
                От него известен пока один ордер{" "}
                <span className="num">{shortId(trade.taker_order_id, 14)}</span>: адрес
                восстанавливается из квитанции транзакции.{" "}
              </>
            )
          )}
          Ниже — соседи по сносу: мейкеры, которых тот же ордер закрыл вместе с нами. Они
          стояли по НАШУ сторону, и с ними мы не торговали.
        </p>
      ) : (
        <p className="text-[var(--color-muted)]">
          Контрагенты: мейкеры, стоявшие против нашего тейкер-ордера.
          {trade.taker_order_id && (
            <>
              {" "}
              Наш ордер <span className="num">{shortId(trade.taker_order_id, 14)}</span>.
            </>
          )}
        </p>
      )}

      {fills.length === 0 ? (
        <p className="text-[var(--color-muted)]">Встречных строк в событии не было.</p>
      ) : (
        <table className="w-full text-xs">
          <thead>
            <tr className="text-left text-[var(--color-muted)]">
              <th className="pb-1 font-normal">{neighbours ? "Сосед по сносу" : "Кошелёк"}</th>
              <th className="pb-1 font-normal">Тир тогда</th>
              <th className="pb-1 text-right font-normal">Размер</th>
              <th className="pb-1 text-right font-normal">Цена</th>
              {/* Сторона и исход строки — то, чем сосед по сносу отличается от
                  контрагента, и на глаз это видно только рядом с нашей строкой:
                  у соседей она совпадает с нашей (все купили Up по 0.84), у
                  контрагентов противоположна. */}
              <th className="pb-1 font-normal">Сторона</th>
              <th className="pb-1 font-normal">Исход</th>
            </tr>
          </thead>
          <tbody>
            {fills.map((f) => {
              const { tier, snapshot } = fillTier(f, tiers);
              return (
                <tr key={f.order_id} className="border-t border-[var(--color-border)]">
                  {/* Две ссылки: наша страница кошелька — «как он торговал
                      против нас», профиль биржи — его позиции и история по всем
                      рынкам, которых у нас нет вовсе. Именно здесь, а не в
                      свёрнутой строке: разбирают контрагента в раскрытой. */}
                  <td className="py-1 pr-3">
                    <span className="inline-flex items-baseline gap-1.5">
                      <Link
                        to={`/wallet/${encodeURIComponent(f.address)}`}
                        onClick={(e) => e.stopPropagation()}
                        title={f.address}
                        className="num hover:text-[var(--color-accent)] hover:underline"
                      >
                        {shortId(f.address, 16)}
                      </Link>
                      <PolymarketProfile address={f.address} />
                    </span>
                  </td>
                  <td className="py-1 pr-3">
                    <Tier
                      tier={tier}
                      title={snapshot ? "квалификация на момент сделки" : "тир на сейчас: снимка не сохранилось"}
                    />
                  </td>
                  <td className="num py-1 pr-3 text-right">{f.size}</td>
                  <td className="num py-1 pr-3 text-right">{f.price}</td>
                  <td
                    className="py-1 pr-3"
                    style={{ color: f.side === "BUY" ? "var(--color-buy)" : "var(--color-sell)" }}
                  >
                    {f.side === "BUY" ? "покупка" : "продажа"}
                  </td>
                  <td className="py-1 text-[var(--color-muted)]">{f.outcome ?? "—"}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}

      {ours.length > 0 && (
        <p className="text-[var(--color-muted)]">
          Наших встречных ордеров в этой сделке: {ours.length} —{" "}
          <span className="num">
            {ours.map((f) => `${f.size} @ ${f.price}`).join(", ")}
          </span>
          . Сделка целиком: <span className="num">{trade.size}</span> @{" "}
          <span className="num">{trade.price}</span> — это тейкер-ордер, а не наш филл.
        </p>
      )}
    </div>
  );
}
